import { z } from 'zod';
import { localizedStringSchema } from '../localized-string.js';
import { locationSchema } from './booking.js';
import { emailSchema, localeSchema, objectIdSchema, timeZoneSchema } from './common.js';
import { bookingStatusSchema, paymentStatusSchema } from './public-booking.js';

/**
 * DTOs for the public API at /v1.
 *
 * These are a contract with software we do not control: Zapier steps, Make
 * scenarios, generated SDKs and directory listings bind to these field names.
 * So they are deliberately *not* the internal host DTOs, which are free to
 * change with the app. Two rules follow:
 *
 *   - Only add fields. Removing or renaming one breaks somebody's automation
 *     silently, months later, and /v1 promises additive change only.
 *   - Never widen them by accident. Internal fields (syncStatus, syncError,
 *     bookerEmailFailed, eventTypeColour, hostNotes) are omitted on purpose,
 *     so the app can change them freely.
 *
 * User-authored text appears twice: `name` resolved to a plain string in the
 * account's locale, and `nameI18n` with the raw object. Integration platforms
 * need a plain string for dropdowns and field mapping; bilingual hosts need
 * the original.
 */

export const API_SCOPES = [
  'event_types:read',
  'bookings:read',
  'bookings:write',
  'webhooks:manage',
] as const;
export const apiScopeSchema = z.enum(API_SCOPES);
export type ApiScope = z.infer<typeof apiScopeSchema>;

/** What a key created as "read only" gets. */
export const READ_ONLY_SCOPES: ApiScope[] = ['event_types:read', 'bookings:read'];

export const apiMoneySchema = z.object({
  /** Minor units, or whole yen: JPY is zero-decimal, so 5000 is ¥5,000. */
  amount: z.number().int(),
  currency: z.string().length(3),
});

// ---------------------------------------------------------------------------
// GET /me
// ---------------------------------------------------------------------------

export const apiAccountSchema = z.object({
  id: z.string(),
  name: z.string(),
  email: z.string(),
  handle: z.string().nullable(),
  locale: localeSchema,
  timezone: z.string(),
  bookingPageUrl: z.string().nullable(),
  plan: z.enum(['free', 'standard']),
  scopes: z.array(apiScopeSchema),
});
export type ApiAccount = z.infer<typeof apiAccountSchema>;

// ---------------------------------------------------------------------------
// Event types
// ---------------------------------------------------------------------------

export const apiEventTypeSchema = z.object({
  id: z.string(),
  name: z.string(),
  nameI18n: localizedStringSchema,
  description: z.string().nullable(),
  slug: z.string(),
  durationMinutes: z.number().int(),
  visibility: z.enum(['public', 'invite_only']),
  active: z.boolean(),
  /** Null when the event type is invite-only or hidden: its address would 404. */
  bookingUrl: z.string().nullable(),
  price: apiMoneySchema.nullable(),
  payOnArrival: z.boolean(),
  locationKind: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type ApiEventType = z.infer<typeof apiEventTypeSchema>;

// ---------------------------------------------------------------------------
// Bookings
// ---------------------------------------------------------------------------

export const apiBookingSchema = z.object({
  id: z.string(),
  status: bookingStatusSchema,
  start: z.string(),
  end: z.string(),
  hostTimezone: z.string(),
  /**
   * The workspace member taking this booking, or null when the account owner
   * takes it themselves — which is every booking on a page that is not shared.
   */
  agent: z.object({ id: z.string(), name: z.string() }).nullable().default(null),
  eventType: z.object({
    id: z.string(),
    name: z.string(),
    nameI18n: localizedStringSchema,
    slug: z.string(),
  }),
  booker: z.object({
    name: z.string(),
    email: z.string(),
    phone: z.string().nullable(),
    locale: localeSchema,
    timezone: z.string(),
  }),
  answers: z.array(
    z.object({
      questionId: z.string(),
      label: z.string(),
      labelI18n: localizedStringSchema,
      value: z.union([z.string(), z.boolean()]),
    }),
  ),
  bookerNotes: z.string().nullable(),
  location: locationSchema,
  joinUrl: z.string().nullable(),
  source: z.enum(['public', 'manual', 'reschedule']),
  payment: z
    .object({
      amount: z.number().int(),
      currency: z.string(),
      status: paymentStatusSchema,
      refundedAmount: z.number().int(),
      receiptUrl: z.string().nullable(),
    })
    .nullable(),
  cancellation: z
    .object({
      by: z.enum(['booker', 'host', 'system']),
      reason: z.string().nullable(),
      at: z.string(),
    })
    .nullable(),
  /*
    No rescheduledFromId or rescheduledToId.

    The spec's example carries both. In this codebase rescheduling mutates the
    booking in place rather than cancelling one and creating another, so the
    forward pointer has nothing to point at and rescheduledFromId is only ever
    written as null. Publishing a field that is always null would be a promise
    we would then have to keep. `source: 'reschedule'` is the honest signal
    that a booking has moved, and booking.rescheduled carries the previous
    time.
  */
  /** Where the host manages this booking in the app. */
  manageUrl: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type ApiBooking = z.infer<typeof apiBookingSchema>;

// ---------------------------------------------------------------------------
// Requests
// ---------------------------------------------------------------------------

export const apiPaginationSchema = z.object({
  limit: z.coerce.number().int().min(1).max(100).default(50),
  cursor: z.string().optional(),
});

export const apiListEventTypesQuerySchema = apiPaginationSchema.extend({
  active: z
    .enum(['true', 'false'])
    .default('true')
    .transform((v) => v === 'true'),
});

/**
 * The window, as UTC dates.
 *
 * Said out loud because the obvious reading is wrong for anyone west of UTC:
 * `from` becomes 00:00:00Z and `to` becomes 23:59:59Z, so "the 6th" is the UTC
 * 6th rather than the host's. A host in Los Angeles asking for one date gets
 * the tail of the previous local evening and loses the end of the day they
 * meant — with no error, just a list that quietly covers the wrong hours.
 */
export const apiSlotsQuerySchema = z.object({
  from: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .describe('Start of the window, as a UTC date. Interpreted as 00:00:00Z on that date.'),
  to: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .describe('End of the window, as a UTC date. Interpreted as 23:59:59Z on that date.'),
});

export const API_BOOKING_SORTS = ['-createdAt', 'start', '-start', '-updatedAt'] as const;

export const apiListBookingsQuerySchema = apiPaginationSchema.extend({
  /**
   * Comma-separated. Omitted, it excludes the in-flight states.
   *
   * Described here rather than only in the comment, because this is what the
   * published document shows an integrator: a bare "string" tells them nothing
   * about the separator or the words it accepts.
   */
  status: z
    .string()
    .optional()
    .describe(
      'Comma-separated list of statuses, e.g. "confirmed,completed". One or more of: held, awaiting_payment, confirmed, cancelled, completed, no_show. Omitted, it returns confirmed, cancelled, completed and no_show — everything except the in-flight states a booking passes through before it is anyone\u2019s business.',
    ),
  eventTypeId: objectIdSchema.optional(),
  email: emailSchema.optional(),
  startFrom: z.string().datetime().optional(),
  startTo: z.string().datetime().optional(),
  updatedSince: z.string().datetime().optional(),
  /** Newest first by default: Zapier's polling dedupe depends on it. */
  sort: z.enum(API_BOOKING_SORTS).default('-createdAt'),
});

/** Statuses a booking has to reach before an integrator should see it. */
export const API_DEFAULT_BOOKING_STATUSES = [
  'confirmed',
  'cancelled',
  'completed',
  'no_show',
] as const;

export const apiCreateBookingSchema = z.object({
  eventTypeId: objectIdSchema,
  start: z.string().datetime(),
  booker: z.object({
    name: z.string().trim().min(1).max(120),
    email: emailSchema,
    phone: z.string().trim().max(40).optional(),
    locale: localeSchema.default('en'),
    timezone: timeZoneSchema,
  }),
  notes: z.string().trim().max(2000).optional(),
  notify: z.boolean().default(true),
});

/**
 * No `notify`: cancelBooking cannot suppress the booker's email, and a silent
 * cancellation is not something an integration should be able to do. The API
 * cancels exactly as the app does.
 */
export const apiCancelBookingSchema = z.object({
  message: z.string().trim().max(1000).optional(),
});

export const apiListResponse = <T extends z.ZodTypeAny>(item: T) =>
  z.object({ items: z.array(item), nextCursor: z.string().nullable() });

// ---------------------------------------------------------------------------
// Errors
// ---------------------------------------------------------------------------

/**
 * Every `code` a /v1 response can carry. Published, so an integrator can write
 * an error branch against it, which means it is a promise: the API translates
 * its internal codes into this list rather than letting them through, and a
 * test pins the translation. Additive like the rest of /v1 — a new code may
 * appear, so clients must have a fallback branch.
 */
export const PUBLIC_API_ERROR_CODES = [
  'validation_failed',
  'unauthorized',
  'insufficient_scope',
  'not_found',
  'slot_unavailable',
  'event_type_inactive',
  'booking_state_changed',
  'duplicate_booking',
  'idempotency_conflict',
  'rate_limited',
  'bad_request',
  'internal_error',
] as const;
export type PublicApiErrorCode = (typeof PUBLIC_API_ERROR_CODES)[number];

/**
 * The public envelope. Nested under `error` so a client can tell a failure from
 * a success by shape alone, which several connector platforms rely on. The
 * internal API's flat `apiErrorSchema` (schemas/common.ts) is a different
 * thing and stays as it is.
 */
export const publicApiErrorSchema = z.object({
  error: z.object({
    // A string rather than an enum: a client that switches on an unknown code
    // must fall through, not fail to parse the response.
    code: z.string().describe(`One of: ${PUBLIC_API_ERROR_CODES.join(', ')}.`),
    message: z.string(),
    details: z.unknown().optional(),
  }),
});
export type PublicApiError = z.infer<typeof publicApiErrorSchema>;

// ---------------------------------------------------------------------------
// Webhooks
// ---------------------------------------------------------------------------

export const API_WEBHOOK_EVENTS = [
  'booking.created',
  'booking.rescheduled',
  'booking.cancelled',
  'booking.no_show',
  'booking.completed',
] as const;
export const apiWebhookEventSchema = z.enum(API_WEBHOOK_EVENTS);
export type ApiWebhookEvent = z.infer<typeof apiWebhookEventSchema>;

export const apiCreateWebhookSchema = z.object({
  url: z.string().url().max(2000),
  /** `*` subscribes to everything, including events added later. */
  events: z
    .array(z.union([apiWebhookEventSchema, z.literal('*')]))
    .min(1)
    .max(20),
  description: z.string().trim().max(200).optional(),
});

export const apiWebhookSchema = z.object({
  id: z.string(),
  url: z.string(),
  events: z.array(z.string()),
  description: z.string().nullable(),
  status: z.enum(['active', 'disabled']),
  /** How it was created, so the UI can say "created by Zapier". */
  createdVia: z.string(),
  lastSuccessAt: z.string().nullable(),
  consecutiveFailures: z.number().int(),
  createdAt: z.string(),
});
export type ApiWebhook = z.infer<typeof apiWebhookSchema>;

/** The secret is returned exactly once, by the create call. */
export const apiWebhookCreatedSchema = apiWebhookSchema.extend({ secret: z.string() });
